import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { AccessContext } from '../access/access.ts';
import {
  hasAnyRole,
  menuFor,
  ROLE_LABEL,
  navFor,
  pageFromSearch,
  pageSummary,
  PAGES,
  rolesLabel,
  type Area,
  type NavRole,
  type PageId,
  type PageTarget,
} from '../access/roles.ts';
import { Button } from '../components/Button.tsx';
import { Icon, PAGE_ICON } from '../components/Icon.tsx';
import { useMenuMode } from '../components/MenuMode.tsx';
import { MENU_TOGGLE_ATTR, SideNav } from './SideNav.tsx';
import { PageBoundary } from '../components/PageBoundary.tsx';
import { ErrorState, Loading, PageHeader, PoweredBy } from '../components/States.tsx';
import { HelpPanel } from '../help/HelpPanel.tsx';
import type { Tenant } from '../tenant/tenant.ts';
import { ClientsScreen } from '../quotes/ClientsScreen.tsx';
import { ContractsScreen } from '../quotes/ContractsScreen.tsx';
import { QuotesScreen } from '../quotes/QuotesScreen.tsx';
import { IntegrationsScreen } from '../integrations/IntegrationsScreen.tsx';
import { UsersScreen } from '../users/UsersScreen.tsx';
import { PeopleScreen } from '../people/PeopleScreen.tsx';
import { CompanyScreen } from '../company/CompanyScreen.tsx';
import { CompanyAuditScreen } from '../audit/CompanyAuditScreen.tsx';
import { AircraftScreen } from '../aircraft/AircraftScreen.tsx';
import { hubCrewLoader } from '../staff/api.ts';
import { settleMyStaffInvites } from '../staff/invite.ts';
import { localToday } from '../ops/format.ts';
import {
  canManageLicences,
  canPortal,
  canReadAuditLog,
  staffReportLabel,
  supportBannerWords,
} from '@teslyn/domain';
import { noteSupportView, openSupport } from '../support/api.ts';
import { useSupportWatch } from '../support/useSupportWatch.ts';
import { previewAudit, type Scope } from '../portal/api.ts';
import { ClientUsersScreen } from '../portal/ClientUsersScreen.tsx';
import { PreviewScreen, type PreviewTarget } from '../portal/PreviewScreen.tsx';
import { ProjectsScreen, projectTabOf } from '../portal/ProjectsScreen.tsx';
import { ReportsScreen } from '../portal/ReportsScreen.tsx';
import { StationHealthScreen } from '../stations/StationHealthScreen.tsx';
import { SpaceWeatherScreen } from '../spaceweather/SpaceWeatherScreen.tsx';
import { HazardPacksScreen } from '../hazard-packs/HazardPacksScreen.tsx';
import { DeliverablesScreen } from '../deliverables/DeliverablesScreen.tsx';
import { ClientApprovals } from '../approvals/ApprovalsTab.tsx';
import { SafetyRegisterScreen } from '../safety/SafetyRegisterScreen.tsx';
import { SafetySignoffScreen } from '../safety/SafetySignoffScreen.tsx';
import { OpsScreen } from '../ops/OpsScreen.tsx';
import { LiveTrackingScreen } from '../tracking/LiveTrackingScreen.tsx';
import { ReportEmailsChoice } from '../setup/ReportEmailsChoice.tsx';
import { NoticesScreen } from '../notices/NoticesScreen.tsx';
import { noticeProjectLink, reportsProjectLink } from '../notices/api.ts';
import { SecurityScreen } from '../mfa/SecurityScreen.tsx';
import { AlertsScreen } from '../alerts/AlertsScreen.tsx';
import { alertsLink } from '../alerts/push.ts';
import { ExportsScreen } from '../exports/ExportsScreen.tsx';
import { HomePassphraseReminder } from '../exports/passphrase.tsx';
import { DiagnosticsScreen } from '../diagnostics/DiagnosticsScreen.tsx';
import { GetMobileScreen } from '../mobile-app/GetMobile.tsx';
import { LicencesScreen } from '../licences/LicencesScreen.tsx';
import { AdminTenantsScreen } from '../admin/AdminTenantsScreen.tsx';
import { AdminInvoicesScreen } from '../admin/AdminInvoicesScreen.tsx';
import { AdminAuditScreen } from '../admin/AdminAuditScreen.tsx';
import { AdminDevicesScreen } from '../admin/AdminDevicesScreen.tsx';

export interface ShellProps {
  readonly sb: SupabaseClient;
  readonly tenant: Tenant;
  readonly userId: string;
  readonly email: string;
  readonly context: AccessContext;
  /** Present when the user has more than one place to work on this portal. */
  readonly onSwitch?: () => void;
  readonly onSignOut: () => void;
  readonly redirectTo: string;
  /** Tenant admins and managers: start Preview as client. */
  readonly onPreview?: (t: PreviewTarget) => void;
  /** Set while a staff member previews a client organisation: `context` is then that organisation's. */
  readonly preview?: { readonly onEnd: () => void };
  /** The session's two-step level changed (a code entered on the Security page). */
  readonly onSessionChanged?: () => void;
}

export function areaOf(ctx: AccessContext): Area {
  return ctx.kind;
}

/** The roles that decide what this context shows: a staff member's tenant roles, else the single client/platform role. */
export function rolesOf(ctx: AccessContext): readonly NavRole[] {
  if (ctx.kind === 'platform') return ['platform_admin'];
  return ctx.kind === 'workspace' ? ctx.roles : [ctx.role];
}

const AREA_LABEL: Record<Area, string> = {
  workspace: 'Workspace',
  client: 'Client portal',
  platform: 'Platform',
};

const HELP_IDS: Partial<Record<PageId, string>> = {
  users: 'cloud.users-roles',
  clients: 'cloud.clients',
  quotes: 'cloud.quotes',
  contracts: 'cloud.contracts',
  company: 'cloud.company-documents',
  integrations: 'cloud.integrations',
  projects: 'cloud.projects',
  reports: 'cloud.reports',
  stations: 'cloud.station-health',
  spaceweather: 'cloud.space-weather',
  'hazard-packs': 'cloud.hazard-packs',
  deliverables: 'cloud.deliverables',
  'safety-signoff': 'cloud.safety-signoff',
  'safety-register': 'cloud.safety-register',
  preview: 'cloud.preview-as-client',
  ops: 'cloud.operations-hub',
  people: 'cloud.staff',
  tracking: 'cloud.live-tracking',
  aircraft: 'cloud.aircraft',
  security: 'cloud.security',
  alerts: 'cloud.alerts',
  exports: 'cloud.data-exports',
  diagnostics: 'cloud.diagnostics',
  'mobile-app': 'cloud.teslyn-mobile',
  licences: 'cloud.licences',
  tenants: 'cloud.admin-console',
  invoicing: 'cloud.admin-console',
  audit: 'cloud.admin-console',
};

const CLIENT_HELP_IDS: Partial<Record<PageId, string>> = {
  security: 'cloud.security',
  notices: 'cloud.client-notices',
  users: 'cloud.client-users',
  projects: 'cloud.projects',
  reports: 'cloud.reports',
  deliverables: 'cloud.deliverables',
  approvals: 'cloud.client-approvals',
};

function helpIdFor(page: PageId, area: Area): string {
  // the company's own Audit log (R166); the console's page keeps the console's help
  if (area === 'workspace' && page === 'audit') return 'cloud.audit-log';
  const id =
    area === 'client'
      ? CLIENT_HELP_IDS[page]
      : area === 'workspace' || area === 'platform'
        ? HELP_IDS[page]
        : undefined;
  if (id) return id;
  return area === 'client' ? 'cloud.client-home' : 'cloud.workspace-home';
}

/**
 * The help of a tab or view with a page of its own (decision 427 Photos, R24 Refly list, parity C4 Map, C3 Diurnal
 * QC; since the menu consolidation the Flights tab, the Deleted list, People's and Company's tabs).
 */
const SECTION_HELP: Readonly<Record<string, string>> = {
  'projects:photos': 'cloud.photos',
  'projects:safety': 'cloud.safety-documents',
  'projects:approvals': 'cloud.client-approvals',
  'projects:refly': 'cloud.refly-list',
  'projects:diurnal': 'cloud.diurnal-qc',
  'projects:map': 'cloud.portal-map',
  'projects:flights': 'cloud.flights',
  'projects:setup': 'cloud.project-setup',
  'projects:deleted': 'cloud.deleted-projects',
  'people:signin': 'cloud.users-roles',
  'company:settings': 'cloud.settings',
  'company:final-report': 'cloud.final-report-text',
  'projects:final-report': 'cloud.final-report-text',
  'company:support': 'cloud.support-access',
  'company:emails': 'cloud.email-log',
};

/** Role-aware shell: top bar with tenant logo, side navigation limited to the role, page, help, footer credit. */
export function Shell({
  sb,
  tenant,
  userId,
  email,
  context,
  onSwitch,
  onSignOut,
  redirectTo,
  onPreview,
  preview,
  onSessionChanged,
}: ShellProps): ReactElement {
  // a push notice or an alert email opens `?page=alerts&alert=<id>` (docs/specs/cloud/alerts.md)
  const [link] = useState(() => alertsLink(window.location.search));
  // a client notice opens `?page=projects&project=<id>&tab=<tab>` (docs/specs/cloud/alerts.md §11)
  const [linkedProject] = useState(() => noticeProjectLink(window.location.search));
  // a report email, a client notice of a new report or a report's "Email…" link opens `?page=reports&project=<id>`
  // (R198): Reports opens on that project, until this person leaves the page (then their own choice)
  const [reportsLink, setReportsLink] = useState(() => reportsProjectLink(window.location.search));
  // `?page=<id>` opens that page, an old page's id its new place (MOVED_PAGES; the menu consolidation); a page this
  // person may not open shows Home
  const [target, setTarget] = useState<PageTarget>(() => {
    const start = pageFromSearch(window.location.search, areaOf(context));
    const allowed =
      start !== null &&
      navFor(rolesOf(context)).includes(start.page) &&
      (start.page !== 'alerts' || context.kind === 'workspace');
    return allowed ? start : { page: 'home' };
  });
  const page = target.page;
  if (reportsLink !== null && page !== 'reports') setReportsLink(null);
  const setPage = useCallback((p: PageId) => setTarget({ page: p }), []);
  // decision 1021: the safety register's "Open the project's height breach report" opens that project's Safety tab
  const [breachProject, setBreachProject] = useState<string | null>(null);
  if (breachProject !== null && page !== 'projects') setBreachProject(null);
  const [openContract, setOpenContract] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // the left menu pinned, icons only (the default) or hidden, each person's own choice (R10; MB, 25 Oct 2026)
  const [menuMode, setMenuMode] = useMenuMode(userId);
  const icons = menuMode === 'icons';
  // hidden: the top bar's menu button opens the menu for a moment, over the page; hidden stays the choice
  const [menuPeek, setMenuPeek] = useState(false);
  if (menuPeek && menuMode !== 'hidden') setMenuPeek(false);
  // the tab or view shown inside the page (a project's tab, the Deleted list, People's and Company's tabs), for the
  // help panel
  const [section, setSection] = useState<string | null>(null);
  const onSection = useCallback((tab: string | null) => setSection(tab), []);
  const tabHelp = section ? (SECTION_HELP[`${page}:${section}`] ?? null) : null;
  // the Operations Hub cards' crew (decision 422): project crew from the staff register, in force today
  const loadCrew = useMemo(() => hubCrewLoader(sb, () => localToday()), [sb]);
  const area = areaOf(context);
  const roles = rolesOf(context);
  // Previewing a client's portal shows what the client sees, not the staff member's own Security page.
  const nav = preview ? navFor(roles).filter((p) => p !== 'security') : navFor(roles);
  // the menu under its headings (C9, decision 855); "My sign-in" in the top bar (C7) except in Braynewave's console
  const menu = menuFor(nav, area);
  const signInInTopBar = area !== 'platform' && nav.includes('security');
  const label = rolesLabel(roles);
  // Clients see the client portal title; staff and platform see the tenant's portal title (MB, 18 Oct 2026).
  const title = area === 'client' ? tenant.clientTitle : tenant.productTitle;
  useEffect(() => {
    document.title = title;
  }, [title]);
  // An invitation from Invite to Teslyn or Users & roles links the staff register person on acceptance (decision 660):
  // the sign-in does it in the database; asking once here covers a sign-in the database did not see.
  const staffSignIn = context.kind === 'workspace' && !preview;
  useEffect(() => {
    if (staffSignIn) void settleMyStaffInvites(sb);
  }, [sb, userId, staffSignIn]);
  // Teslyn Support access (decision 800): open the grant first (recorded), then record each page viewed
  const support = context.kind === 'workspace' ? (context.support ?? null) : null;
  const supportTenant = support && context.kind === 'workspace' ? context.tenantId : null;
  const [supportOpen, setSupportOpen] = useState<'opening' | 'open' | string>(() =>
    support && !support.standing ? 'opening' : 'open',
  );
  useEffect(() => {
    if (!supportTenant || support?.standing) return;
    openSupport(sb, supportTenant)
      .then(() => setSupportOpen('open'))
      .catch((e: unknown) => setSupportOpen(e instanceof Error ? e.message : String(e)));
  }, [sb, supportTenant, support?.standing]);
  useEffect(() => {
    if (!supportTenant || supportOpen !== 'open') return;
    void noteSupportView(sb, supportTenant, section ? `${page}:${section}` : page).catch(() => undefined);
  }, [sb, supportTenant, supportOpen, page, section]);
  // when the access ends the page goes, with whatever it has read (decision 905: a map keeps its aircraft otherwise)
  const supportWatch = useSupportWatch(
    sb,
    supportTenant && !support?.standing && supportOpen === 'open' ? supportTenant : null,
    support?.endsAt ?? null,
  );
  const where =
    context.kind === 'client'
      ? context.clientOrgName
      : context.kind === 'workspace'
        ? context.tenantName
        : 'Braynewave support';

  function body(): ReactElement {
    if (context.kind === 'client') {
      const scope: Scope = { kind: 'client', clientOrgId: context.clientOrgId };
      const onReportOpened = preview
        ? (r: Parameters<typeof staffReportLabel>[0] & { readonly id: string }) =>
            void previewAudit(sb, context.clientOrgId, 'report_opened', {
              report_id: r.id,
              report: staffReportLabel(r),
            }).catch(() => undefined)
        : undefined;
      const opened = onReportOpened ? { onReportOpened } : {};
      if (page === 'projects') {
        return (
          <ProjectsScreen
            sb={sb}
            scope={scope}
            mode="client"
            initialTab={projectTabOf(target.tab)}
            initialProjectId={linkedProject}
            onTabChange={onSection}
            preview={Boolean(preview)}
            clientRole={context.role}
            {...opened}
          />
        );
      }
      if (page === 'reports')
        return (
          <ReportsScreen
            sb={sb}
            scope={scope}
            mode="client"
            personId={userId}
            linkedProjectId={reportsLink}
            {...opened}
          />
        );
      if (page === 'deliverables') return <DeliverablesScreen sb={sb} scope={scope} mode="client" />;
      // R191 (decision 1010 item 2): every request of the organisation, contracts made before their project too
      if (page === 'approvals')
        return (
          <section className="page">
            <PageHeader title="Approvals" subtitle={context.clientOrgName} />
            <ClientApprovals
              sb={sb}
              clientOrgId={context.clientOrgId}
              projectId={null}
              clientRole={context.role}
              preview={Boolean(preview)}
            />
          </section>
        );
      // R190 (decision 1010): what this person is told about the organisation's projects, by email and push
      if (page === 'notices')
        return (
          <NoticesScreen
            sb={sb}
            userId={userId}
            email={email}
            clientOrgId={context.clientOrgId}
            clientOrgName={context.clientOrgName}
            tenantName={tenant.name}
            preview={Boolean(preview)}
          />
        );
      if (page === 'users' && context.role === 'client_admin') {
        return (
          <ClientUsersScreen
            sb={sb}
            tenantId={context.tenantId}
            clientOrgId={context.clientOrgId}
            clientOrgName={context.clientOrgName}
            currentUserId={userId}
            redirectTo={redirectTo}
            readOnly={Boolean(preview)}
          />
        );
      }
    }
    if (context.kind === 'workspace' && nav.includes(page)) {
      const scope: Scope = { kind: 'tenant', tenantId: context.tenantId };
      if (page === 'projects') {
        return (
          <ProjectsScreen
            sb={sb}
            scope={scope}
            mode="staff"
            roles={context.roles}
            tenantSlug={context.tenantSlug}
            initialTab={projectTabOf(target.tab)}
            initialProjectId={breachProject ?? linkedProject}
            {...(target.tab === 'deleted' ? { initialView: 'deleted' as const } : {})}
            onTabChange={onSection}
            {...(nav.includes('clients') ? { onOpenClients: () => setPage('clients') } : {})}
            onOpenPage={(p: PageId) => {
              if (nav.includes(p)) setPage(p);
            }}
            openablePages={nav}
            {...(nav.includes('contracts')
              ? {
                  onOpenContract: (id: string) => {
                    setOpenContract(id);
                    setPage('contracts');
                  },
                }
              : {})}
          />
        );
      }
      if (page === 'reports') {
        return (
          <ReportsScreen
            sb={sb}
            scope={scope}
            mode="staff"
            roles={context.roles}
            personId={userId}
            linkedProjectId={reportsLink}
          />
        );
      }
      // decisions 839, 843 (ADR-008): share links and the processors' zips (R165)
      if (page === 'deliverables')
        return <DeliverablesScreen sb={sb} scope={scope} mode="staff" roles={context.roles} />;
      // staff only (docs/specs/cloud/station-health.md): no client role has the page, and the database gives clients
      // no station
      if (page === 'stations')
        return <StationHealthScreen sb={sb} tenantId={context.tenantId} roles={context.roles} />;
      // staff only (decisions 688, 691; R115): no client role has the page, and the provider proxy refuses clients
      // space weather
      if (page === 'spaceweather')
        return <SpaceWeatherScreen sb={sb} tenantId={context.tenantId} userId={userId} />;
      // staff only (docs/specs/cloud/hazard-packs.md, decision 625): no client role has the page, and the database
      // gives clients no pack
      if (page === 'hazard-packs')
        return <HazardPacksScreen sb={sb} tenantId={context.tenantId} tenantName={context.tenantName} />;
      // staff only (R119, R120; docs/specs/cloud/docuseal.md): no client role has the page, and the database gives
      // clients no sign-off request
      if (page === 'safety-signoff')
        return (
          <SafetySignoffScreen
            sb={sb}
            tenantId={context.tenantId}
            tenantName={context.tenantName}
            roles={context.roles}
          />
        );
      // staff only (decision 837; docs/specs/safety-register.md): no client role has the page, and the database gives
      // clients no entry
      if (page === 'safety-register')
        return (
          <SafetyRegisterScreen
            sb={sb}
            tenantId={context.tenantId}
            tenantName={context.tenantName}
            roles={context.roles}
            {...(nav.includes('projects')
              ? {
                  onOpenBreachReport: (projectId: string) => {
                    setBreachProject(projectId);
                    setTarget({ page: 'projects', tab: 'safety' });
                  },
                }
              : {})}
          />
        );
      // staff only (decision 842; docs/specs/cloud/live-tracking.md): tenant admins, managers and field crew have the
      // page; the project functions it reads refuse clients
      if (page === 'tracking')
        return <LiveTrackingScreen sb={sb} tenantId={context.tenantId} roles={context.roles} />;
      if (page === 'preview' && onPreview && canPortal(context.roles, 'portal.preview')) {
        return <PreviewScreen sb={sb} tenantId={context.tenantId} onStart={onPreview} />;
      }
    }
    if (page === 'security' && nav.includes('security')) {
      return (
        <SecurityScreen
          sb={sb}
          title={area === 'platform' ? 'Security' : 'My sign-in'}
          {...(context.kind === 'workspace' && context.roles.includes('tenant_admin')
            ? { adminTenantId: context.tenantId }
            : {})}
          {...(onSessionChanged ? { onSessionChanged } : {})}
          {...(context.kind === 'client' && !preview
            ? {
                reportEmails: (
                  <ReportEmailsChoice
                    sb={sb}
                    userId={userId}
                    clientOrgId={context.clientOrgId}
                    clientOrgName={context.clientOrgName}
                    tenantName={tenant.name}
                  />
                ),
              }
            : {})}
        />
      );
    }
    if (context.kind === 'workspace' && page === 'alerts' && nav.includes('alerts')) {
      return (
        <AlertsScreen
          sb={sb}
          tenantId={context.tenantId}
          userId={userId}
          roles={context.roles}
          openAlertId={link.alertId}
        />
      );
    }
    if (context.kind === 'workspace' && page === 'exports' && context.roles.includes('tenant_admin')) {
      return (
        <ExportsScreen
          sb={sb}
          tenantId={context.tenantId}
          tenantName={context.tenantName}
          roles={context.roles}
          openPassphrase={target.tab === 'passphrase'}
        />
      );
    }
    // every staff role (docs/specs/mobile/mobile-distribution.md §3): the phone app's download and its QR code
    if (context.kind === 'workspace' && page === 'mobile-app') return <GetMobileScreen sb={sb} />;
    if (context.kind === 'workspace' && page === 'diagnostics' && context.roles.includes('tenant_admin')) {
      return <DiagnosticsScreen sb={sb} tenantId={context.tenantId} />;
    }
    // R166, decision 927: tenant admins and managers read their own company's audit log (audit_select)
    if (context.kind === 'workspace' && page === 'audit' && canReadAuditLog(context.roles)) {
      return <CompanyAuditScreen sb={sb} tenantId={context.tenantId} tenantName={context.tenantName} />;
    }
    // decisions 295, 447, 838: tenant admins only; the policies give nobody else the seats, devices or invoices
    if (context.kind === 'workspace' && page === 'licences' && canManageLicences(context.roles)) {
      return <LicencesScreen sb={sb} tenantId={context.tenantId} tenantName={context.tenantName} />;
    }
    if (context.kind === 'platform') {
      if (page === 'tenants') return <AdminTenantsScreen sb={sb} />;
      if (page === 'devices') return <AdminDevicesScreen sb={sb} />;
      if (page === 'invoicing') return <AdminInvoicesScreen sb={sb} />;
      if (page === 'audit') return <AdminAuditScreen sb={sb} />;
    }
    if (page === 'home') {
      return area === 'client' ? (
        <ClientHome context={context} tenant={tenant} onOpen={setPage} nav={nav} />
      ) : (
        <WorkspaceHome
          label={label}
          where={where}
          onOpen={setPage}
          nav={nav}
          area={area}
          notice={
            // decision 880: tenant admins are reminded to set the backup passphrase until one is set
            context.kind === 'workspace' &&
            context.roles.includes('tenant_admin') &&
            nav.includes('exports') ? (
              <HomePassphraseReminder
                sb={sb}
                tenantId={context.tenantId}
                roles={context.roles}
                onSet={() => setTarget({ page: 'exports', tab: 'passphrase' })}
              />
            ) : null
          }
        />
      );
    }
    if (page === 'ops' && nav.includes('ops') && context.kind !== 'client') {
      // The Operations Hub (docs/specs/portal/operations-hub.md): staff of the tenant, or a platform admin on
      // the tenant's portal; the Ops functions refuse everyone else.
      const tenantId = context.kind === 'workspace' ? context.tenantId : tenant.id;
      if (!tenantId) {
        return <Placeholder title="Operations Hub" text="This tenant's record could not be read." />;
      }
      return (
        <OpsScreen
          sb={sb}
          tenantId={tenantId}
          tenantName={context.kind === 'workspace' ? context.tenantName : tenant.name}
          loadCrew={loadCrew}
        />
      );
    }
    if (page === 'users' && hasAnyRole(roles, 'tenant_admin', 'platform_admin')) {
      const tenantId = context.kind === 'workspace' ? context.tenantId : tenant.id;
      if (!tenantId) {
        return <Placeholder title="Users & roles" text="This tenant's record could not be read." />;
      }
      return <UsersScreen sb={sb} tenantId={tenantId} currentUserId={userId} redirectTo={redirectTo} />;
    }
    if (context.kind === 'workspace' && nav.includes(page)) {
      const common = { sb, tenantId: context.tenantId, userId, roles: context.roles } as const;
      if (page === 'clients') return <ClientsScreen {...common} />;
      if (page === 'quotes') {
        return (
          <QuotesScreen
            {...common}
            onOpenContract={(id) => {
              setOpenContract(id);
              setPage('contracts');
            }}
          />
        );
      }
      if (page === 'contracts')
        return <ContractsScreen key={openContract ?? 'list'} {...common} initialContractId={openContract} />;
      // C5 (decision 844): the staff register for every staff role, sign-in and roles for tenant admins
      if (page === 'people')
        return (
          <PeopleScreen
            sb={sb}
            tenantId={context.tenantId}
            userId={userId}
            roles={context.roles}
            redirectTo={redirectTo}
            initialTab={target.tab ?? null}
            onTabChange={onSection}
          />
        );
      if (page === 'aircraft')
        return (
          <AircraftScreen
            sb={sb}
            tenantId={context.tenantId}
            tenantName={context.tenantName}
            roles={context.roles}
          />
        );
      if (page === 'integrations')
        return <IntegrationsScreen sb={sb} tenantId={context.tenantId} roles={context.roles} />;
      // C6 (decision 844): Company and documents, and (tenant admins) Settings, as tabs
      if (page === 'company') {
        return (
          <CompanyScreen
            sb={sb}
            tenantId={context.tenantId}
            tenantSlug={context.tenantSlug}
            tenantName={context.tenantName}
            tenant={tenant}
            userId={userId}
            roles={context.roles}
            initialTab={target.tab ?? null}
            onTabChange={onSection}
          />
        );
      }
    }
    const def = PAGES[page];
    return <Placeholder title={def.label} text={pageSummary(page, area)} />;
  }

  return (
    <div
      className={[
        'app',
        menuMode === 'hidden' ? 'app--no-nav' : 'app--nav',
        icons ? 'app--nav-icons' : '',
        helpOpen ? 'app--help' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-area={area}
    >
      <header className="topbar">
        {menuMode === 'hidden' && (
          <Button
            variant="quiet"
            className="topbar__menu"
            aria-label="Show menu"
            title="Show menu"
            aria-expanded={menuPeek}
            aria-controls="side-menu"
            {...{ [MENU_TOGGLE_ATTR]: '' }}
            onClick={() => setMenuPeek((v) => !v)}
          >
            <Icon name="panel-left" />
          </Button>
        )}
        {tenant.logo ? (
          <img className="topbar__logo topbar__logo--tenant" src={tenant.logo} alt={tenant.name} />
        ) : (
          <span className="heading">{tenant.name}</span>
        )}
        <span className="heading topbar__title">{title}</span>
        <span className="chip chip--brand caption topbar__area">{AREA_LABEL[area]}</span>
        <span className="topbar__spacer" />
        <span className="caption muted topbar__who">
          {email} · {label}
          {context.kind === 'client' ? ` · ${context.clientOrgName}` : ''}
        </span>
        {onSwitch && (
          <Button variant="quiet" onClick={onSwitch}>
            Switch organisation
          </Button>
        )}
        <Button variant="quiet" onClick={() => setHelpOpen((o) => !o)} aria-pressed={helpOpen}>
          Help
        </Button>
        {/* C7 (decision 844): your own two-step sign-in, beside Sign out; its icon alone on a phone */}
        {signInInTopBar && (
          <Button
            variant="quiet"
            className="topbar__signin"
            aria-label="My sign-in"
            title="My sign-in: your two-step sign-in"
            aria-current={page === 'security' ? 'page' : undefined}
            onClick={() => {
              setSection(null);
              setPage('security');
            }}
          >
            <Icon name={PAGE_ICON.security} />
            <span className="topbar__signin-word">My sign-in</span>
          </Button>
        )}
        <Button onClick={onSignOut}>Sign out</Button>
      </header>
      {(menuMode !== 'hidden' || menuPeek) && (
        <SideNav
          id="side-menu"
          label={AREA_LABEL[area]}
          items={menu.flatMap((g) =>
            g.pages.map((id, i) => ({
              id,
              label: PAGES[id].label,
              icon: PAGE_ICON[id],
              ...(g.label && i === 0 ? { heading: g.label } : {}),
            })),
          )}
          current={page}
          mode={menuMode}
          onMode={setMenuMode}
          onDismiss={(why) => {
            setMenuPeek(false);
            if (why === 'escape') document.querySelector<HTMLElement>(`[${MENU_TOGGLE_ATTR}]`)?.focus();
          }}
          onSelect={(id) => {
            setOpenContract(null);
            setSection(null);
            setPage(id);
          }}
        />
      )}
      <main className="main">
        {preview && context.kind === 'client' && (
          <div className="preview-banner" role="status">
            <p className="body">
              <strong>Preview:</strong> you are seeing {context.clientOrgName}&apos;s portal as a{' '}
              {ROLE_LABEL[context.role]}. Nothing can be changed.
            </p>
            <Button onClick={preview.onEnd}>End preview</Button>
          </div>
        )}
        {support && context.kind === 'workspace' && !supportWatch.ended && (
          <div className="preview-banner" role="status">
            <p className="body">
              <strong>Teslyn Support:</strong>{' '}
              {support.standing
                ? `standing access (staging only) to ${context.tenantName}: every change you make is recorded for them.`
                : supportBannerWords(context.tenantName, support.scope, supportWatch.endsAt)}
            </p>
          </div>
        )}
        {/* a page's render error shows "This page could not load" on that page only; a new page starts afresh */}
        {supportWatch.ended && context.kind === 'workspace' ? (
          <div className="preview-banner" role="alert">
            <p className="body">
              <strong>Teslyn Support:</strong> your access to {context.tenantName} has ended. Nothing of
              theirs is shown any more; reload this page if they grant access again.
            </p>
          </div>
        ) : supportOpen === 'open' ? (
          <PageBoundary key={`${page}:${target.tab ?? ''}`}>{body()}</PageBoundary>
        ) : supportOpen === 'opening' ? (
          <Loading what="Teslyn Support access" />
        ) : (
          <ErrorState message={supportOpen} />
        )}
      </main>
      {helpOpen && (
        <HelpPanel
          pageId={tabHelp ?? helpIdFor(page, area)}
          roles={roles}
          tenantName={tenant.name}
          productTitle={title}
          onClose={() => setHelpOpen(false)}
        />
      )}
      <footer className="footer">
        <PoweredBy />
        <span className="caption muted">{tenant.name}</span>
      </footer>
    </div>
  );
}

function Placeholder({ title, text }: { readonly title: string; readonly text: string }): ReactElement {
  return (
    <section className="page">
      <PageHeader title={title} />
      <div className="panel">
        <p className="label muted">Coming soon</p>
        <p className="body">{text}</p>
      </div>
    </section>
  );
}

/**
 * The Home cards (C10, decision 844): under the menu's headings, each page with its menu icon (R124: PAGE_ICON, the
 * one list of page icons) and what it does now. Home itself and the top bar's "My sign-in" have no card.
 */
function PageLinks({
  nav,
  area,
  onOpen,
}: {
  readonly nav: readonly PageId[];
  readonly area: Area;
  readonly onOpen: (p: PageId) => void;
}): ReactElement {
  const groups = menuFor(nav, area)
    .map((g) => ({ ...g, pages: g.pages.filter((id) => id !== 'home') }))
    .filter((g) => g.pages.length > 0);
  return (
    <div className="stack home-groups">
      {groups.map((g) => (
        <section key={g.label ?? 'pages'} className="stack" aria-label={g.label ?? 'Pages'}>
          {g.label && <h2 className="heading">{g.label}</h2>}
          <ul className="tiles">
            {g.pages.map((id) => (
              <li key={id} className="tile">
                <button type="button" className="tile__btn" onClick={() => onOpen(id)}>
                  <span className="tile__title">
                    <Icon name={PAGE_ICON[id]} />
                    <span className="heading">{PAGES[id].label}</span>
                  </span>
                  <span className="body muted">{pageSummary(id, area)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function WorkspaceHome({
  label,
  where,
  nav,
  area,
  onOpen,
  notice = null,
}: {
  readonly label: string;
  readonly where: string;
  readonly nav: readonly PageId[];
  readonly area: Area;
  readonly onOpen: (p: PageId) => void;
  /** Something the person should do first (the backup passphrase reminder, decision 880). */
  readonly notice?: ReactElement | null;
}): ReactElement {
  return (
    <section className="page">
      <PageHeader title="Workspace" subtitle={`${where} · signed in as ${label}`} />
      <p className="body">
        {area === 'platform'
          ? 'Braynewave’s console: every organisation on Teslyn, their laptops, invoices and the audit log.'
          : 'Each card says what its page does, under the same headings as the menu. Your roles decide which pages you see; your own two-step sign-in is under “My sign-in” in the top bar.'}
      </p>
      {notice}
      <PageLinks nav={nav} area={area} onOpen={onOpen} />
    </section>
  );
}

function ClientHome({
  context,
  tenant,
  nav,
  onOpen,
}: {
  readonly context: AccessContext;
  readonly tenant: Tenant;
  readonly nav: readonly PageId[];
  readonly onOpen: (p: PageId) => void;
}): ReactElement {
  const org = context.kind === 'client' ? context.clientOrgName : '';
  return (
    <section className="page">
      <PageHeader title={`Welcome, ${org}`} subtitle={`Your survey projects with ${tenant.name}`} />
      <p className="body">
        Your projects&apos; progress, maps and the reports {tenant.name} publishes to you. Each card says what
        its page does; your own two-step sign-in is under &ldquo;My sign-in&rdquo; in the top bar.
      </p>
      <PageLinks nav={nav} area="client" onOpen={onOpen} />
    </section>
  );
}
